import { viderSaisieAuClic } from '@/lib/brouillon';
import { deconnecter } from '@/lib/deconnexion';
import { AppShell, type NavItem, type RenderLink } from '@pms-digital/design-system';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ID_CONTENU } from './lien-evitement';

/** The routes the shell links to: `Link` wants a known path, the package hands over a plain `href`. */
type CheminCoquille = '/' | '/historique' | '/profil';

/**
 * Adapts the `AppShell` of the PMS design system (56 px header with the logo, 56 px icon rail,
 * full-screen menu under 768 px, skip link and `main` target) to what Rosetta needs around it. No
 * styling lives here: it only plugs the router and the draft into the package.
 *
 * - `renderLink` renders the `Link` of the router.
 * - `onNavigate` empties the input when the home page is reached (#26), from the logo, the home
 *   entry of the rail or the one of the menu under 768 px. A click with a modifier key keeps the
 *   browser's own behaviour (`viderSaisieAuClic`, which reads the click `onNavigate` receives).
 * - The package ties `aria-current` to the highlight. The home entry is highlighted everywhere
 *   but on `/historique` and `/profil`, yet it is the current page on `/` only: the attribute is
 *   removed anywhere else, as before.
 * - The skip link and its target are the package's (`skipLinkLabel`, `mainId`).
 * - The avatar at the bottom of the rail opens the package's profile menu: « profil » leads to the
 *   profile page, « se déconnecter » signs out as the button of that page does (`deconnecter`, then
 *   `/login`).
 * - The `main` of the package is a flex column that fills the height under the header: a screen
 *   that pins its footer to the bottom does so with no wrapper of its own.
 */
export function Coquille({ children }: { children: ReactNode }) {
  const chemin = useRouterState({ select: (etat) => etat.location.pathname });
  const navigate = useNavigate();
  const surHistorique = chemin === '/historique';
  const surProfil = chemin === '/profil';

  const nav: NavItem[] = [
    {
      id: 'accueil',
      label: 'traduire',
      icon: 'home',
      href: '/',
      active: !surHistorique && !surProfil,
    },
    {
      id: 'historique',
      label: 'historique',
      icon: 'history',
      href: '/historique',
      active: surHistorique,
    },
  ];

  const renderLink: RenderLink = ({
    href,
    'aria-current': courant,
    children: contenu,
    ...reste
  }) => {
    const versAccueil = href === '/';
    return (
      <Link
        to={href as CheminCoquille}
        {...reste}
        {...(versAccueil && chemin !== '/'
          ? {}
          : courant === undefined
            ? {}
            : { 'aria-current': courant })}
      >
        {contenu}
      </Link>
    );
  };

  return (
    <AppShell
      nav={nav}
      profile={{
        label: 'profil',
        href: '/profil',
        active: surProfil,
        signOutLabel: 'se déconnecter',
        onSignOut: () => {
          void deconnecter().then(() => navigate({ to: '/login' }));
        },
      }}
      homeHref="/"
      renderLink={renderLink}
      skipLinkLabel="aller au contenu"
      mainId={ID_CONTENU}
      onNavigate={(href, evenement) => {
        if (href === '/') viderSaisieAuClic(evenement);
      }}
    >
      {children}
    </AppShell>
  );
}
